import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useLogoutMutation } from '@/features/auth';
import { useWalletBalanceQuery } from '@/features/dashboard';
import { useProfileQuery } from '@/features/profile/api/useProfileQueries';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import {
  getUserDisplayName,
  getUserInitial,
} from '@/shared/lib/user/displayName';
import { getProfilePicUrl } from '@/shared/lib/user/profilePic';
import { CityChangeModal } from '@/features/location/components/CityChangeModal';
import { CampaignModeSelectModal } from '@/features/campaigns/components/CampaignModeSelectModal';
import { setCity, setSearch } from '@/shared/store/slices/filtersSlice';
import { formatCompactMoney } from '@/shared/lib/format/money';
import { prefetchScreensList } from '@/features/billboards/lib/prefetchScreensList';
import { AppSidebar } from '@/layouts/components/AppSidebar';
import { AppHeaderSearch } from '@/layouts/components/AppHeaderSearch';
import {
  formatRoleLabel,
  useAppNavGroups,
} from '@/layouts/hooks/useAppNavGroups';
import { useIsClient } from '@/shared/hooks/useIsClient';
import { useIsTeamMember } from '@/shared/hooks/useIsTeamMember';
import { UserAvatar } from '@/shared/ui/UserAvatar';
import { routes } from '@/shared/config/routes';

export function AppLayout() {
  const user = useAppSelector((state) => state.auth.user);
  const logout = useLogoutMutation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isClient = useIsClient();
  const isTeamMember = useIsTeamMember();
  const hideWallet = isClient || isTeamMember;
  const { data: walletRes } = useWalletBalanceQuery({ enabled: !hideWallet });
  const profile = useProfileQuery();
  const storeCity = useAppSelector((s) => s.filters.city);
  const storeSearch = useAppSelector((s) => s.filters.search);
  const groups = useAppNavGroups(user);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [cityModalOpen, setCityModalOpen] = useState(false);
  const [campaignModeOpen, setCampaignModeOpen] = useState(false);
  const [query, setQuery] = useState(storeSearch);
  const [searchError, setSearchError] = useState(false);

  useEffect(() => {
    setQuery(storeSearch);
  }, [storeSearch]);

  // Screens list API is slow (~5s); warm cache on dashboard shell mount.
  useEffect(() => {
    void prefetchScreensList(storeCity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const state = location.state as { openCampaignCreate?: boolean } | null;
    if (state?.openCampaignCreate) {
      if (!isClient) setCampaignModeOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate, isClient]);

  const displayName = getUserDisplayName(user);
  const initial = getUserInitial(user);
  const avatarUrl =
    getProfilePicUrl(profile.data?.data?.profilePic) ||
    getProfilePicUrl(user?.profilePic);
  const balance = walletRes?.data?.availableBalance ?? 0;
  const cityLabel = storeCity || 'Bhopal';
  const roleLabel = formatRoleLabel(user?.role);

  const clearSearch = () => {
    setQuery('');
    setSearchError(false);
    dispatch(setSearch(''));
  };

  const runSearch = (event?: { preventDefault?: () => void }) => {
    event?.preventDefault?.();
    const keyword = query.trim();
    if (!keyword) {
      setSearchError(true);
      return;
    }
    setSearchError(false);
    dispatch(setSearch(keyword));
    if (location.pathname !== '/screens') {
      navigate('/screens');
    }
  };

  const handleCitySelect = (nextCity: string) => {
    dispatch(setCity(nextCity));
    setCityModalOpen(false);
    if (location.pathname !== '/screens') {
      navigate('/screens');
    }
  };

  const sidebar = (
    <AppSidebar
      groups={groups}
      displayName={displayName}
      roleLabel={roleLabel}
      initial={initial}
      avatarUrl={avatarUrl}
      city={storeCity}
      onCloseMobile={() => setMobileOpen(false)}
      onLogout={() => {
        if (logout.isPending) return;
        logout.mutate(undefined, {
          onSettled: () => navigate('/login', { replace: true }),
        });
      }}
      isLoggingOut={logout.isPending}
    />
  );

  const isCampaignCreate = location.pathname.startsWith('/campaigns/create');

  return (
    <div
      className={`dash-shell flex ${
        isCampaignCreate
          ? 'h-[100dvh] max-h-[100dvh] overflow-hidden'
          : 'min-h-[100dvh]'
      }`}
    >
      <div className="sticky top-0 z-20 hidden h-[100dvh] shrink-0 lg:block">
        {sidebar}
      </div>

      {mobileOpen ? (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 border-none bg-[#0F1B2D]/40"
            onClick={() => setMobileOpen(false)}
            aria-label="Close sidebar overlay"
          />
          <div className="absolute inset-y-0 left-0 shadow-2xl">{sidebar}</div>
        </div>
      ) : null}

      <div
        className={`flex min-w-0 flex-1 flex-col ${
          isCampaignCreate ? 'h-[100dvh] max-h-[100dvh] overflow-hidden' : ''
        }`}
      >

        <header className="sticky top-0 z-20 flex h-[76px] items-center gap-3 border-b border-[#f1eff9] bg-white/80 backdrop-blur-md px-4 sm:px-6 lg:px-8">
          <button
            type="button"
            className="rounded-xl border border-[#f1eff9] bg-white p-2 text-[var(--dash-muted)] lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M4 6h16M4 12h16M4 18h16"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>

          <AppHeaderSearch
            cityLabel={cityLabel}
            query={query}
            searchError={searchError}
            onQueryChange={(value) => {
              setQuery(value);
              if (searchError) setSearchError(false);
            }}
            onClear={clearSearch}
            onSubmit={runSearch}
            onOpenCityModal={() => setCityModalOpen(true)}
          />

          <div className="ml-auto flex items-center gap-3">
            {!isClient ? (

          <button
                type="button"
                onClick={() => setCampaignModeOpen(true)}
                className="dash-btn-gradient hidden h-[42px] items-center gap-1.5 rounded-full px-5 text-[14px] font-bold text-white shadow-[0_8px_20px_-8px_rgba(59,102,184,0.5)] transition hover:brightness-105 sm:inline-flex"
              >
                <span className="text-[14px] leading-none">+</span>
                New Campaign
              </button>
            ) : null}
            {!hideWallet ? (
              <button
                type="button"
                onClick={() => navigate('/wallet')}
                className="flex h-[42px] items-center rounded-full bg-[#ddf5ea] px-4 text-[13px] font-extrabold text-[#12785a] transition hover:brightness-95"
              >
                {formatCompactMoney(balance)}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => navigate(routes.profile)}
              aria-label="My profile"
              className="rounded-full ring-offset-2 transition hover:ring-2 hover:ring-[var(--dash-brand)]/30 focus-visible:ring-2 focus-visible:ring-[var(--dash-brand)]/40 focus-visible:outline-none"
            >
              <UserAvatar
                url={avatarUrl}
                initial={initial}
                className="h-[42px] w-[42px] text-[13px]"
                alt={displayName}
              />
            </button>
          </div>
        </header>

        <main
          className={`flex-1 min-h-0 ${
            isCampaignCreate
              ? 'flex flex-col overflow-hidden p-3 sm:p-4 lg:px-6 lg:py-2.5'
              : 'overflow-y-auto p-4 sm:p-6 lg:p-8'
          } ${
            !isClient && !isCampaignCreate
              ? 'pb-24 sm:pb-6 lg:pb-8'
              : ''
          }`}
        >
          <Outlet />
        </main>
      </div>

      {/* Mobile FAB — header CTA is sm+ only */}
      {!isClient && !mobileOpen && !campaignModeOpen && !location.pathname.startsWith('/campaigns/create') ? (
        <button
          type="button"
          onClick={() => setCampaignModeOpen(true)}
          aria-label="New Campaign"
          className="dash-btn-gradient fixed right-4 bottom-[max(1.25rem,env(safe-area-inset-bottom))] z-30 inline-flex items-center gap-2 rounded-full px-5 py-3.5 text-sm font-semibold text-white shadow-[0_12px_28px_-8px_rgba(20,94,140,0.55)] transition active:scale-[0.97] sm:hidden"
        >
          <span className="text-lg leading-none">+</span>
          New Campaign
        </button>
      ) : null}

      <CityChangeModal
        open={cityModalOpen}
        selectedCity={cityLabel}
        onClose={() => setCityModalOpen(false)}
        onSelect={handleCitySelect}
      />

      <CampaignModeSelectModal
        open={campaignModeOpen && !isClient}
        onClose={() => setCampaignModeOpen(false)}
      />
    </div>
  );
}
